import type { ModuleMigrationDef } from "@kubdee/sdk";
import type { CapabilityName } from "../capabilities";

// Issue #249 M7: typed constant — a typo becomes a compile error.
const CAPABILITIES = JSON.stringify(["commerce"] satisfies CapabilityName[]);

/**
 * Seed the `shopee` connector type row (P10 — issue #244).
 *
 * - Idempotent via `INSERT OR IGNORE`: re-running the migration won't clobber
 *   operator edits (icon / label tweaks, capability additions).
 * - Primary key `id` equals `slug` so the capability registry and webhook
 *   ingress route both address the row by the same string. This matches
 *   the FB / Line / IG / Google Calendar / Gmail seeds (migrations 002–006).
 * - `oauth_config.primaryMode = 'partner_auth'` — Shopee is NOT standard OAuth.
 *   The authorize flow signs a partner-level request (partner_id + partner_key +
 *   timestamp → HMAC-SHA256 hex `sign`), and the token endpoint requires the
 *   `code` + `shop_id` from the shop owner's redirect — not `grant_type`. The
 *   frontend wizard keys off `primaryMode` to present the right UX (redirect to
 *   Shopee, then capture `?code=` + `?shop_id=` from the callback).
 * - `oauth_config` carries:
 *     - authorize path (`/api/v2/shop/auth_partner`) — joined to the base at
 *       runtime so non-SG regions can point at a regional partner host.
 *     - token paths for exchange + refresh.
 *     - env var names for the partner-level secrets (operator-level, not
 *       per-instance — partner_id / partner_key authorise EVERY shop the
 *       partner app has been linked to).
 *     - `refreshRotates: true` — reminder that Shopee rotates the refresh
 *       token on every refresh, so persistence logic must write both the new
 *       access_token AND the new refresh_token atomically.
 * - `webhook_config.signatureHeader = 'authorization'` — Shopee puts the
 *   HMAC-SHA256 signature there (NOT `x-shopee-signature` — Shopee's only
 *   adapter that uses the standard `Authorization` header for HMAC instead
 *   of a bearer token). Algorithm HMAC-SHA256, encoding base64.
 * - `icon: 'ShoppingBag'` — surfaced via `packages/ui/src/lib/icon-registry.ts`
 *   (added to `extraIcons` alongside the seed).
 * - `color: '#EE4D2D'` — Shopee brand orange.
 * - `created_at` / `updated_at` use a fixed seed timestamp — tenants-local
 *   time isn't available at seed time, and the column is audit-only.
 */
export const migration: ModuleMigrationDef = {
  key: "007_shopee_seed",
  version: "0.7.0",
  sql: `
INSERT OR IGNORE INTO \`connectors_types\` (
  id, slug, name, icon, icon_url, capabilities, oauth_config, webhook_config,
  is_active, created_at, updated_at
) VALUES (
  'shopee',
  'shopee',
  'Shopee',
  'ShoppingBag',
  NULL,
  '${CAPABILITIES}',
  '{"primaryMode":"partner_auth","apiBase":"https://partner.shopeemobile.com","authorizePath":"/api/v2/shop/auth_partner","tokenExchangePath":"/api/v2/auth/token/get","tokenRefreshPath":"/api/v2/auth/access_token/get","partnerIdEnv":"SHOPEE_PARTNER_ID","partnerKeyEnv":"SHOPEE_PARTNER_KEY","accessTokenTtlSec":14400,"refreshTokenTtlSec":2592000,"refreshRotates":true,"notes":"Shopee is NOT standard OAuth. Partner auth signs a partner-level HMAC-SHA256 request and redirects the shop owner; the callback carries ?code= + ?shop_id=. Refresh rotates the refresh token — persist both access_token and refresh_token atomically. Every API call signs {partner_id}{api_path}{timestamp}{access_token}{shop_id} with the partner_key. access_token MUST go in the URL query — Shopee ignores Authorization headers."}',
  '{"signatureHeader":"authorization","algorithm":"HMAC-SHA256","encoding":"base64","baseStringTemplate":"{url}|{body}","partnerKeyEnv":"SHOPEE_PARTNER_KEY","eventCodes":{"1":"commerce.order.updated","3":"commerce.logistics.updated","8":"commerce.product.updated","10":"commerce.shop.deauthorized"},"notes":"Push V2. Verify with HMAC-SHA256({url}|{body}, partner_key) = base64(sig). Code=1 carries only order_sn; the dispatcher fetches the full order via get_order_detail before emitting the typed commerce.order.updated event."}',
  1,
  '2026-04-17T00:00:00Z',
  '2026-04-17T00:00:00Z'
);
  `.trim(),
};
